import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import * as Rx from 'rxjs';
import * as i0 from "@angular/core";
import * as i1 from "@angular/router";
import * as i2 from "@nguniversal/express-engine/tokens";
/*
 * Reactive & server-side friendly version of window.location
 * See https://developer.mozilla.org/en-US/docs/Web/API/Location
 */
var Location = /** @class */ (function () {
    function Location(router, platformId, request) {
        this.router = router;
        this.platformId = platformId;
        this.request = request;
        this.routeLoaded = false;
        this.locationSubject$ = new Rx.ReplaySubject(1);
    }
    /**
     * Emits a stream of LocationEvent
     */
    Location.prototype.valueChanges = function () {
        return this.locationSubject$.asObservable();
    };
    /**
     * SSR-friendly version of Router::navigateByUrl() without query parameters
     *
     * @remark
     *
     * WARNING: Please prefer using `router.navigate` if you need to pass in query
     * params to the router
     */
    Location.prototype.navigateByUrl = function (url) {
        if (isPlatformBrowser(this.platformId)) {
            // tslint:disable-next-line:no-navigate-by-url
            return Rx.from(this.router.navigateByUrl(url));
        }
        this.request._SSR_handleRedirect("" + this.origin + url);
        // tslint:disable-next-line:no-navigate-by-url
        return Rx.from(this.router.navigateByUrl('/internal/blank'));
    };
    /**
     * Sets the location to a provided url. This will leave the webapp, DO NOT
     * use it for internal redirects, use navigateByUrl instead
     */
    Location.prototype.redirect = function (url) {
        if (isPlatformBrowser(this.platformId)) {
            if (this.routeLoaded) {
                window.location.assign(url);
            }
            else {
                window.location.replace(url);
            }
            return Rx.NEVER;
        }
        this.request._SSR_handleRedirect(url);
        // tslint:disable-next-line:no-navigate-by-url
        return Rx.from(this.router.navigateByUrl('/internal/blank'));
    };
    Object.defineProperty(Location.prototype, "origin", {
        /*
         * Returns the current origin in a platform friendly way
         */
        get: function () {
            if (isPlatformBrowser(this.platformId)) {
                return window.location.origin;
            }
            return this.request.protocol + "://" + this.host;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Location.prototype, "host", {
        /*
         * Returns the current host, that is the hostname, a ':', and the port of the
         * URL, in a platform friendly way
         */
        get: function () {
            if (isPlatformBrowser(this.platformId)) {
                return window.location.host;
            }
            // FIXME: we should be using `req.host` here but Express incorrectly strips
            // off the port number, which breaks local dev. This has been fixed in
            // Express 5 -> https://expressjs.com/en/guide/migrating-5.html#req.host
            // Note: X-Forwarded-Host is normally only ever a single value, but Fastly
            // seems to append the values.
            var header = this.request.get('X-Forwarded-Host') ||
                this.request.get('Host');
            var index = header.indexOf(',');
            return index !== -1 ? header.substring(0, index).trim() : header.trim();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Location.prototype, "hostname", {
        /*
         * Returns the current hostname, that is the domain of the URL, in a platform
         * friendly way
         */
        get: function () {
            if (isPlatformBrowser(this.platformId)) {
                return window.location.hostname;
            }
            return this.request.hostname;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Location.prototype, "href", {
        /*
         * Returns the current (absolute) URL in a platform friendly way
         */
        get: function () {
            if (isPlatformBrowser(this.platformId)) {
                return window.location.href;
            }
            return "" + this.origin + this.request.originalUrl;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Location.prototype, "url", {
        /*
         * Returns the current URL in a platform friendly way
         */
        get: function () {
            if (isPlatformBrowser(this.platformId)) {
                return "" + window.location.pathname + window.location.search;
            }
            return this.request.originalUrl;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Location.prototype, "pathname", {
        /**
         * Returns the current pathname in a platform friendly way
         */
        get: function () {
            if (isPlatformBrowser(this.platformId)) {
                return window.location.pathname;
            }
            return this.request.path;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Location.prototype, "protocol", {
        /**
         * Returns the current protocol in a platform friendly way
         */
        get: function () {
            if (isPlatformBrowser(this.platformId)) {
                return window.location.protocol;
            }
            return this.request.protocol;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * [PRIVATE] Exported private API.
     * To be used only by the LocationComponent to push new locations
     */
    Location.prototype._setLocation = function (pathname, hash, search) {
        this.locationSubject$.next({
            hash: hash,
            hostname: this.hostname,
            href: "" + this.origin + pathname,
            origin: this.origin,
            pathname: pathname,
            search: search,
        });
    };
    /**
     * [PRIVATE] exported private API
     * To be used only by the LocationComponent to mark that routes are loaded
     */
    Location.prototype._routeHasLoaded = function () {
        this.routeLoaded = true;
    };
    Location.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function Location_Factory() { return new Location(i0.ɵɵinject(i1.Router), i0.ɵɵinject(i0.PLATFORM_ID), i0.ɵɵinject(i2.REQUEST, 8)); }, token: Location, providedIn: "root" });
    return Location;
}());
export { Location };
